<script setup lang="ts">
import { ALBackgroundGradient, ALHubspotSupport, ALToaster } from '@af-lib/vue-ui';
import mixpanel from 'mixpanel-browser';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import AppHeader from '@/components/AppHeader.vue';
import { useBranding } from '@/composables/useBranding';
import { useHubspotSupport } from '@/composables/useHubspotSupport';
import { useMixpanelPageTracking } from '@/composables/useMixpanelPageTracking';
import { locale } from '@/config/i18n';
import { getMeWithPermissions } from '@/services/auth';
import { getFeatureFlag } from '@/services/public';

const route = useRoute();
const { applyStoredBranding } = useBranding();
const { supportData, onSubmit, onSendChatMessage } = useHubspotSupport();
useMixpanelPageTracking();

const showSupport = ref(false);

const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur']);
const supportPlacementClasses = computed(() => {
  const lang = locale.value.split('-')[0];
  const side = RTL_LANGS.has(lang) ? 'start-5' : 'end-5';
  return `fixed bottom-5 ${side}`;
});

onMounted(async () => {
  applyStoredBranding();

  const blocked = await getFeatureFlag('block-supportform');
  showSupport.value = !blocked;

  // OAuth login tracking — OAuthController sets af-login-method cookie with the provider name
  const match = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith('af-login-method='));
  if (match) {
    const provider = match.split('=')[1] ?? '';
    document.cookie = 'af-login-method=; Max-Age=0; path=/; SameSite=None; Secure';
    try {
      const { user, permissions } = await getMeWithPermissions();
      mixpanel.identify(user.uuid);
      mixpanel.track('login', {
        distinct_id: user.uuid,
        user_id: user.id ?? null,
        user_uuid: user.uuid ?? null,
        company_uuid: user.company_uuid ?? null,
        customer_uuid: user.customer_uuid ?? null,
        email: user.email ?? null,
        isManager: permissions.includes('mng_r'),
        isRecruiter: permissions.includes('rct_r'),
        language: user.locale_label ?? user.locale ?? null,
        login_register_method: provider
      });
    } catch {
      /* non-blocking */
    }
  }
});
</script>

<template>
  <ALBackgroundGradient tag="div" position="center" />

  <main class="flex min-h-screen flex-col font-roboto">
    <AppHeader />

    <div class="flex flex-1 flex-col">
      <RouterView />
    </div>

    <!-- Hidden on /help where a dedicated inline widget is rendered instead -->
    <!-- Hidden when the 'block-supportform' feature flag is active -->
    <ALHubspotSupport
      v-if="showSupport && route.name !== 'help'"
      ref="supportRef"
      :data="supportData"
      :placementClasses="supportPlacementClasses"
      @submit="onSubmit"
      @send-chat-message="onSendChatMessage"
    />

    <!-- Design-system toast container (replaces vue-toastification) -->
    <ALToaster />
  </main>
</template>
